// frontend/src/infrastructure/nativeBridge/NativeBridgePttSource.ts
//
// Czwarte źródło sygnału PTT — czyta zdarzenia kliknięcia z natywnego mostka
// Android (`native-bridge/android/`) przez lokalny serwer HTTP na 127.0.0.1:8842.
// Istnieje wyłącznie dla scenariusza, którego WebHID i Media Session NIE
// obsługują: PRZYCISK PTT PRZY ZGASZONYM/ZABLOKOWANYM EKRANIE. Gdy ekran jest
// włączony, WebHID (`WebHidPttService`) wystarcza — ten kanał nie jest wtedy
// potrzebny, ale nie przeszkadza (po prostu też zgłasza to samo kliknięcie).
//
// POCHODZENIE: 1:1 kopia pliku wzorcowego dostarczonego przez klienta razem z
// mostkiem (`native-bridge/frontend-integration/NativeBridgePttSource.ts`),
// wpiętego w `usePttManager` wg PDF integracyjnego
// (`native-bridge/Integracja_Mostka_PTT.pdf`, sekcja 5). JEDYNA lokalna zmiana
// względem pliku klienta to publiczna metoda `forceReset()` niżej — logika
// odpytywania (start/stop/poll/handleClick) jest nietknięta.
//
// Kontrakt HTTP (patrz `android/app/.../PttBridgeHttpServer.kt`):
//   GET http://127.0.0.1:8842/events?since=<seq>
//   -> { "latestSeq": number, "events": [{ "seq": number, "type": "click", "ts": number }] }
//
// WAŻNE, sprzętowo potwierdzone ograniczenie (patrz PDF, sekcja 3): fizyczne
// gruszki USB testowane do tej pory (Klein VALOR) NIE zgłaszają prawdziwego
// stanu "trzymany" — każde kliknięcie to jeden dyskretny impuls, niezależny od
// czasu trzymania palca. Dlatego ten serwis, tak jak WebHidPttService i
// useBluetoothButton, musi wystawiać interfejs onPress/onRelease — ale
// wewnętrznie każde NOWE zdarzenie "click" z mostka jest traktowane jako
// PRZEŁĄCZNIK: pierwsze wywołuje onPress(), kolejne onRelease(), naprzemiennie.

export interface NativeBridgePttSourceOptions {
  /** Wołane, gdy mostek zgłasza start nasłuchu (pierwsze kliknięcie z pary). */
  onPress: () => void
  /** Wołane, gdy mostek zgłasza koniec nasłuchu (drugie kliknięcie z pary). */
  onRelease: () => void
  /** Adres bazowy mostka. Domyślnie lokalny serwer natywnej appki. */
  baseUrl?: string
  /** Odstęp odpytywania w ms. Domyślnie 700ms — wystarczająco szybko, żeby
   *  nie dodawać zauważalnego opóźnienia, a jednocześnie oszczędnie dla
   *  baterii/CPU. */
  pollIntervalMs?: number
}

export class NativeBridgePttSource {
  private readonly onPress: () => void
  private readonly onRelease: () => void
  private readonly baseUrl: string
  private readonly pollIntervalMs: number

  private pressed = false
  private lastSeq = -1 // -1 = jeszcze nie zainicjalizowany (patrz start())
  private timer: ReturnType<typeof setTimeout> | null = null
  private stopped = true
  private _isConnected = false

  constructor(options: NativeBridgePttSourceOptions) {
    this.onPress = options.onPress
    this.onRelease = options.onRelease
    this.baseUrl = options.baseUrl ?? 'http://127.0.0.1:8842'
    this.pollIntervalMs = options.pollIntervalMs ?? 700
  }

  /** Czy w ostatnim cyklu odpytywania mostek faktycznie odpowiedział (czyli:
   *  appka mostka jest zainstalowana i działa na tym telefonie). Przydatne do
   *  ukrycia/pokazania stanu w UI — brak odpowiedzi NIE jest błędem, tylko
   *  normalnym stanem na telefonach bez zainstalowanego mostka. */
  get isConnected(): boolean {
    return this._isConnected
  }

  start(): void {
    if (!this.stopped) return
    this.stopped = false
    // Pierwsze zapytanie ustala WYŁĄCZNIE bieżący "latestSeq" — nie chcemy
    // odtwarzać historycznych kliknięć sprzed startu appki (ten sam wzorzec,
    // co przy pollingu starszego mostka Bluetooth, patrz PDF sekcja 5).
    void this.poll(true)
  }

  stop(): void {
    this.stopped = true
    if (this.timer !== null) {
      clearTimeout(this.timer)
      this.timer = null
    }
    if (this.pressed) {
      this.pressed = false
      this.onRelease()
    }
  }

  // JEDYNA lokalna zmiana względem pliku wzorcowego klienta (patrz nagłówek).
  // FIX DESYNCHRONIZACJI hardware ↔ software: gdy nagranie domknął watchdog ciszy
  // (VAD), a nie użytkownik, sprzętowy przełącznik w mostku został w pozycji „on".
  // Bez tego kolejne kliknięcie zostałoby zinterpretowane jako `onRelease` i
  // nagrywanie by nie ruszyło. Menedżer woła to po auto-stopie z ciszy — zeruje
  // wewnętrzne lustro stanu BEZ emitowania `onRelease` (to resynchronizacja, nie
  // gest użytkownika). Nie dotyka pętli odpytywania.
  public forceReset(): void {
    this.pressed = false
  }

  private scheduleNext(): void {
    if (this.stopped) return
    this.timer = setTimeout(() => void this.poll(false), this.pollIntervalMs)
  }

  private async poll(isFirstCall: boolean): Promise<void> {
    try {
      const res = await fetch(`${this.baseUrl}/events?since=${this.lastSeq < 0 ? 0 : this.lastSeq}`, {
        method: 'GET',
      })
      if (!res.ok) throw new Error(`HTTP ${res.status}`)
      const json = (await res.json()) as { latestSeq: number; events: Array<{ seq: number; type: string; ts: number }> }
      this._isConnected = true

      if (isFirstCall || this.lastSeq < 0) {
        // Pierwsze wywołanie: zapamiętaj punkt startowy, NIE reaguj na
        // zdarzenia sprzed uruchomienia appki.
        this.lastSeq = json.latestSeq
      } else {
        for (const event of json.events) {
          if (event.seq <= this.lastSeq) continue
          this.lastSeq = event.seq
          if (event.type === 'click') this.handleClick()
        }
      }
    } catch {
      // Mostek niezainstalowany / niedziała / telefon bez USB — cichy no-op.
      // To NORMALNY stan (nie każdy telefon ma zainstalowany mostek), więc
      // nie logujemy błędów przy każdej próbie.
      this._isConnected = false
    } finally {
      this.scheduleNext()
    }
  }

  private handleClick(): void {
    this.pressed = !this.pressed
    if (this.pressed) this.onPress()
    else this.onRelease()
  }
}
